import { lookupService } from './lookupService';
import type { UserDataContext, Client, ClientUnit } from '../../types/api';

/**
 * User Data Service
 * Manages the cascading data flow: clients → units → financial data
 */
export class UserDataService {
  /**
   * Initialize user data by fetching clients and their units
   * Returns the first client and first unit as defaults
   */
  async initializeUserData(): Promise<UserDataContext> {
    try {
      // 1. Get client(s) - returns 1 for client, multiple for admin
      const clients = await lookupService.getClients();
      
      if (clients.length === 0) {
        throw new Error('No clients found for this user');
      }
      
      const defaultClient = clients[0]; // First client
      const user_id_no = defaultClient.user_id_no;
      
      // 2. Get client units using user_id_no
      const clientUnits = await lookupService.getClientUnits({ user_id_no });
      const defaultUnit = clientUnits.length > 0 ? clientUnits[0] : null; // First unit as default
      
      const result = {
        clients,
        selectedClient: defaultClient,
        clientUnits,
        selectedUnit: defaultUnit,
        user_id_no,
        unit_id_no: defaultUnit?.unit_id_no || null
      };
      
      return result;
    } catch (error) {
      console.error('Error initializing user data:', error);
      throw error;
    }
  }

  /**
   * Switch to a different client (admin only)
   */
  async switchClient(client: Client): Promise<Partial<UserDataContext>> {
    try {
      const user_id_no = client.user_id_no;
      
      // Get client units for the selected client
      const clientUnits = await lookupService.getClientUnits({ user_id_no });
      const defaultUnit = clientUnits.length > 0 ? clientUnits[0] : null;
      
      return {
        selectedClient: client,
        clientUnits,
        selectedUnit: defaultUnit,
        user_id_no,
        unit_id_no: defaultUnit?.unit_id_no || null
      };
    } catch (error) {
      console.error('Error switching client:', error);
      throw error;
    }
  }

  /**
   * Switch to a different unit for the current client
   */
  switchUnit(unit: ClientUnit): Partial<UserDataContext> {
    return {
      selectedUnit: unit,
      unit_id_no: unit.unit_id_no
    };
  }

  /**
   * Get financial data parameters for API calls
   */
  getFinancialDataParams(context: UserDataContext): { user_id_no: number; unit_id_no: number } | null {
    if (!context.user_id_no || !context.unit_id_no) {
      return null;
    }
    
    return {
      user_id_no: context.user_id_no,
      unit_id_no: context.unit_id_no
    };
  }

  /**
   * Check if user has multiple clients (admin scenario)
   */
  hasMultipleClients(context: UserDataContext): boolean {
    return context.clients.length > 1;
  }

  /**
   * Check if current client has multiple units
   */
  hasMultipleUnits(context: UserDataContext): boolean {
    return context.clientUnits.length > 1;
  }
}

// Create and export a single instance
export const userDataService = new UserDataService();





